/**
 * Form Validation Utilities
 * Centralized validation functions for form inputs
 */

/**
 * Validate email address format
 * @param {string} email - Email address to validate
 * @returns {Object} { isValid: boolean, message: string }
 */
export function validateEmail(email) {
  if (!email || email.trim() === '') {
    return { isValid: false, message: 'Email is required' };
  }

  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  if (!emailRegex.test(email)) {
    return { isValid: false, message: 'Please enter a valid email address' };
  }

  return { isValid: true, message: '' };
}

/**
 * Validate password and check strength
 * @param {string} password - Password to validate
 * @returns {Object} { isValid: boolean, strength: string, message: string, requirements: object }
 */
export function validatePassword(password) {
  if (!password || password.trim() === '') {
    return {
      isValid: false,
      strength: 'none',
      message: 'Password is required',
      requirements: {
        minLength: false,
        hasUppercase: false,
        hasLowercase: false,
        hasNumber: false,
        hasSpecial: false
      }
    };
  }

  const requirements = {
    minLength: password.length >= 8,
    hasUppercase: /[A-Z]/.test(password),
    hasLowercase: /[a-z]/.test(password),
    hasNumber: /[0-9]/.test(password),
    hasSpecial: /[!@#$%^&*(),.?":{}|<>]/.test(password)
  };

  const requirementsMet = Object.values(requirements).filter(Boolean).length;

  let strength = 'weak';
  if (requirementsMet >= 5) {
    strength = 'strong';
  } else if (requirementsMet >= 3) {
    strength = 'medium';
  }

  const isValid = requirements.minLength && requirementsMet >= 3;

  let message = '';
  if (!isValid) {
    if (!requirements.minLength) {
      message = 'Password must be at least 8 characters';
    } else {
      message = 'Password must include uppercase, lowercase, and numbers';
    }
  }

  return { isValid, strength, message, requirements };
}

/**
 * Validate phone number format
 * @param {string} phone - Phone number to validate
 * @param {string} country - Country code (FR, CA, US)
 * @returns {Object} { isValid: boolean, message: string }
 */
export function validatePhone(phone, country = 'FR') {
  if (!phone || phone.trim() === '') {
    return { isValid: false, message: 'Phone number is required' };
  }

  // Remove all non-digit characters for validation
  const cleanPhone = phone.replace(/\D/g, '');

  const minLengths = {
    FR: 10,
    CA: 10,
    US: 10,
    GB: 10
  };

  if (cleanPhone.length < minLengths[country]) {
    return { isValid: false, message: `Phone number must be at least ${minLengths[country]} digits` };
  }

  // Basic validation: just check if it has enough digits
  // More lenient to handle international formats
  if (cleanPhone.length < 10) {
    return { isValid: false, message: 'Phone number is too short' };
  }

  return { isValid: true, message: '' };
}

/**
 * Validate required field
 * @param {string} value - Value to validate
 * @param {string} fieldName - Name of the field for error message
 * @returns {Object} { isValid: boolean, message: string }
 */
export function validateRequired(value, fieldName = 'This field') {
  if (!value || (typeof value === 'string' && value.trim() === '')) {
    return { isValid: false, message: `${fieldName} is required` };
  }

  return { isValid: true, message: '' };
}

/**
 * Validate that two values match (e.g., password confirmation)
 * @param {string} value1 - First value
 * @param {string} value2 - Second value
 * @param {string} fieldName - Name of the field for error message
 * @returns {Object} { isValid: boolean, message: string }
 */
export function validateMatch(value1, value2, fieldName = 'Values') {
  if (value1 !== value2) {
    return { isValid: false, message: `${fieldName} do not match` };
  }

  return { isValid: true, message: '' };
}

/**
 * Validate minimum length
 * @param {string} value - Value to validate
 * @param {number} minLength - Minimum required length
 * @param {string} fieldName - Name of the field for error message
 * @returns {Object} { isValid: boolean, message: string }
 */
export function validateMinLength(value, minLength, fieldName = 'This field') {
  if (!value || value.length < minLength) {
    return { isValid: false, message: `${fieldName} must be at least ${minLength} characters` };
  }

  return { isValid: true, message: '' };
}

/**
 * Validate maximum length
 * @param {string} value - Value to validate
 * @param {number} maxLength - Maximum allowed length
 * @param {string} fieldName - Name of the field for error message
 * @returns {Object} { isValid: boolean, message: string }
 */
export function validateMaxLength(value, maxLength, fieldName = 'This field') {
  if (value && value.length > maxLength) {
    return { isValid: false, message: `${fieldName} must be no more than ${maxLength} characters` };
  }

  return { isValid: true, message: '' };
}

/**
 * Validate bakery name (for subdomain generation)
 * @param {string} name - Bakery name to validate
 * @returns {Object} { isValid: boolean, message: string }
 */
export function validateBakeryName(name) {
  if (!name || name.trim() === '') {
    return { isValid: false, message: 'Bakery name is required' };
  }

  if (name.length < 2) {
    return { isValid: false, message: 'Bakery name must be at least 2 characters' };
  }

  if (name.length > 50) {
    return { isValid: false, message: 'Bakery name must be no more than 50 characters' };
  }

  return { isValid: true, message: '' };
}

/**
 * Format phone number for display
 * @param {string} phone - Phone number to format
 * @param {string} country - Country code (FR, CA, US)
 * @returns {string} Formatted phone number
 */
export function formatPhoneNumber(phone, country = 'FR') {
  const cleanPhone = phone.replace(/\D/g, '');

  if (country === 'FR') {
    // Format as: 01 23 45 67 89
    return cleanPhone.replace(/(\d{2})(?=\d)/g, '$1 ').trim();
  } else if (country === 'CA' || country === 'US') {
    // Format as: (123) 456-7890
    if (cleanPhone.length === 10) {
      return `(${cleanPhone.slice(0, 3)}) ${cleanPhone.slice(3, 6)}-${cleanPhone.slice(6)}`;
    }
  } else if (country === 'GB') {
    // Format as: 020 1234 5678 or 07123 456789
    if (cleanPhone.length === 11) {
      if (cleanPhone.startsWith('02')) {
        return `${cleanPhone.slice(0, 3)} ${cleanPhone.slice(3, 7)} ${cleanPhone.slice(7)}`;
      }
      return `${cleanPhone.slice(0, 5)} ${cleanPhone.slice(5)}`;
    } else if (cleanPhone.length === 10) {
      return `${cleanPhone.slice(0, 4)} ${cleanPhone.slice(4, 7)} ${cleanPhone.slice(7)}`;
    }
  }

  return phone;
}

/**
 * Validate all form fields at once
 * @param {Object} fields - Object with field values
 * @param {Object} rules - Validation rules for each field
 * @returns {Object} { isValid: boolean, errors: object }
 */
export function validateForm(fields, rules) {
  const errors = {};
  let isValid = true;

  Object.keys(rules).forEach(fieldName => {
    const value = fields[fieldName];
    const fieldRules = rules[fieldName];

    for (const rule of fieldRules) {
      const result = rule(value);
      if (!result.isValid) {
        errors[fieldName] = result.message;
        isValid = false;
        break; // Stop at first error for this field
      }
    }
  });

  return { isValid, errors };
}
